import React, { useCallback, useMemo, useState, useEffect, useRef } from "react";
import Table from "../../../core/common/dataTable/index";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import TooltipOption from "../../../core/common/tooltipOption";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { useReactToPrint } from "react-to-print";
import { useSelector } from "react-redux";
import {
  getAllMailTemplates,
  updateMailTemplate,
  deleteMailTemplate,
  getEmailPlaceholders,
  getEmailTypes,
  type GetAllTemplatesParams,
  type MailTemplateFormData,
} from "../../../service/mailTemplate";
import { getAllClasses } from "../../../service/classes";
import { selectSystems } from "../../../core/data/redux/slice/systemsSlice";
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice";
import { campusBrandName, campusBranchLabel } from "../../../utils/campusLogos";

const EmailTemplatesList = () => {
  const routes = all_routes;
  const systems = useSelector(selectSystems);
  const currentBranch = useSelector(selectCurrentBranch);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<any[]>([]);
  const componentRef = useRef<HTMLDivElement>(null);
  const [classNameById, setClassNameById] = useState<Record<string, string>>(
    {}
  );

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    limit: 10,
  });

  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [templateToDelete, setTemplateToDelete] = useState<any>(null);
  const [emailTypes, setEmailTypes] = useState<{ id: string; name: string }[]>(
    []
  );
  const [availableVariables, setAvailableVariables] = useState<
    { label: string; value: string }[]
  >([]);

  const [formData, setFormData] = useState<MailTemplateFormData>({
    name: "",
    subject: "",
    htmlContent: "",
    category: "",
    variables: [],
    classes: [],
    isActive: true,
    file: null,
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const resolvedClassNames = useMemo(() => {
    return (classes: any[] | undefined | null): string[] => {
      if (!Array.isArray(classes) || classes.length === 0) return [];
      return classes
        .map((c) => {
          if (!c) return "";
          if (typeof c === "string") return classNameById[c] || c;
          if (typeof c === "object") {
            const id = (c as any)._id;
            const label =
              (c as any).name ||
              (c as any).classIdentifier ||
              (id && classNameById[id]) ||
              id;
            return label || "";
          }
          return "";
        })
        .filter(Boolean);
    };
  }, [classNameById]);

  const fetchAllData = useCallback(async () => {
    try {
      setLoading(true);
      const params: GetAllTemplatesParams = {
        page: pagination.currentPage,
        limit: pagination.limit,
      };

      const templatesResponse = await getAllMailTemplates(params);
      if (templatesResponse.success) {
        setTemplates(templatesResponse.data);
        setPagination(templatesResponse.pagination);
      }

      const typesResponse = await getEmailTypes();
      if (typesResponse.success) {
        const transformedTypes = typesResponse.data.map((type) => ({
          id: type.id,
          name: type.label,
        }));
        setEmailTypes(transformedTypes);
      }

      const placeholdersResponse = await getEmailPlaceholders();
      if (placeholdersResponse.success) {
        setAvailableVariables(placeholdersResponse.data);
      }

      // Classes (for rendering template -> class names)
      const systemId = systems?.[0]?._id;
      const classesResponse = await getAllClasses({
        limit: 200,
        status: "active",
        ...(systemId ? { system: systemId } : {}),
      });
      const classesArray = classesResponse?.classes || [];
      if (Array.isArray(classesArray) && classesArray.length > 0) {
        const map: Record<string, string> = {};
        classesArray.forEach((cls: any) => {
          if (!cls?._id) return;
          map[cls._id] = cls.name || cls.classIdentifier || cls._id;
        });
        setClassNameById(map);
      } else {
        setClassNameById({});
      }
    } catch (error: any) {
      console.error("Error fetching data:", error);
      toast.error(error.response?.data?.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, pagination.limit, systems]);

  // Fetch all data
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);
  const handlePaginationChange = (page: number, pageSize: number) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: page,
      limit: pageSize,
    }));
  };

  const handleDeleteClick = (template: any) => {
    setTemplateToDelete(template);
    setShowDeleteModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedTemplate(null);
    setFormData({
      name: "",
      subject: "",
      htmlContent: "",
      category: "",
      variables: [],
      classes: [],
      isActive: true,
      file: null,
    });
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setTemplateToDelete(null);
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleVariableSelect = (variable: string) => {
    if (!formData.variables.includes(variable)) {
      setFormData({
        ...formData,
        variables: [...formData.variables, variable],
        htmlContent: formData.htmlContent + ` ${variable}`,
      });
    }
  };

  const removeVariable = (index: number) => {
    const newVariables = [...formData.variables];
    newVariables.splice(index, 1);
    setFormData({
      ...formData,
      variables: newVariables,
    });
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = "Template name is required";
    }
    if (!formData.subject.trim()) {
      errors.subject = "Email subject is required";
    }
    if (!formData.htmlContent.trim()) {
      errors.htmlContent = "Email body is required";
    }
    if (!formData.category) {
      errors.category = "Email type is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUpdateTemplate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedTemplate) return;

    if (!validateForm()) {
      toast.error("Please fill all required fields correctly");
      return;
    }

    try {
      setLoading(true);
      const response = await updateMailTemplate(selectedTemplate._id, formData);

      if (response.success) {
        toast.success("Email template updated successfully!");

        closeEditModal();
        await fetchAllData();
      }
    } catch (error: any) {
      console.error("Error updating template:", error);
      toast.error(error.response?.data?.message || "Failed to update template");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;

    try {
      setLoading(true);
      const response = await deleteMailTemplate(templateToDelete._id);

      if (response.success) {
        toast.success("Template has been deleted.");

        closeDeleteModal();
        await fetchAllData();
      }
    } catch (error: any) {
      console.error("Error deleting template:", error);
      toast.error(error.response?.data?.message || "Failed to delete template");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = useReactToPrint({ contentRef: componentRef });

  const handleExportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(
      templates.map((template) => ({
        "Template ID": template._id,
        Name: template.name,
        "Email Type": template.category,
        Subject: template.subject,
        Classes:
          resolvedClassNames(template.classes).length > 0
            ? resolvedClassNames(template.classes).join(", ")
            : "All Classes",
        Status: template.isActive ? "Active" : "Inactive",
        "Created Date": new Date(template.createdAt).toLocaleDateString(),
        "Last Updated": new Date(template.updatedAt).toLocaleDateString(),
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Email Templates");
    XLSX.writeFile(wb, "email-templates.xlsx");
  };

  const columns = [
    {
      title: "Template Name",
      dataIndex: "name",
      render: (text: string, record: any) => (
        <div>
          <div className="fw-bold">{text}</div>
          <div className="text-muted fs-12">{record.category}</div>
        </div>
      ),
      sorter: true,
    },
    {
      title: "Subject",
      dataIndex: "subject",
      sorter: true,
    },
    {
      title: "Variables",
      dataIndex: "variables",
      render: (variables: string[]) => (
        <div className="d-flex flex-wrap gap-1">
          {variables?.slice(0, 3).map((variable, index) => (
            <span key={index} className="badge badge-soft-primary">
              {variable}
            </span>
          ))}
          {variables?.length > 3 && (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id={`tooltip-${variables.length}`}>
                  {variables.slice(3).join(", ")}
                </Tooltip>
              }
            >
              <span className="badge badge-soft-secondary">
                +{variables.length - 3} more
              </span>
            </OverlayTrigger>
          )}
        </div>
      ),
    },
    {
      title: "Classes",
      dataIndex: "classes",
      render: (classes: any[], record: any) => {
        const names = resolvedClassNames(classes || record?.classes);
        if (names.length === 0) {
          return <span className="text-muted fs-12">All Classes</span>;
        }

        return (
          <span className="fs-12" style={{ whiteSpace: "normal" }}>
            {names.join(", ")}
          </span>
        );
      },
      sorter: false,
    },
    {
      title: "Status",
      dataIndex: "isActive",
      render: (isActive: boolean) => (
        <span
          className={`badge badge-soft-${
            isActive ? "success" : "danger"
          } d-inline-flex align-items-center`}
        >
          <i className="ti ti-circle-filled fs-5 me-1"></i>
          {isActive ? "Active" : "Inactive"}
        </span>
      ),
      sorter: true,
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      render: (date: string) => new Date(date).toLocaleDateString(),
      sorter: true,
    },
    {
      title: "Actions",
      dataIndex: "action",
      render: (text: string, record: any) => (
        <div className="d-flex gap-2">
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Edit Template</Tooltip>}
          >
            <Link
              to={`/email-templates/edit/${record._id}`}
              className="btn btn-outline-primary btn-sm"
            >
              <i className="ti ti-edit"></i>
            </Link>
          </OverlayTrigger>
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Delete Template</Tooltip>}
          >
            <button
              className="btn btn-outline-danger btn-sm"
              onClick={() => handleDeleteClick(record)}
            >
              <i className="ti ti-trash"></i>
            </button>
          </OverlayTrigger>
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Email Templates List</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="#">Settings</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Email Templates List
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <TooltipOption
              onRefresh={fetchAllData}
              onExportPdf={handlePrint}
              onExportExcel={handleExportExcel}
            />
            <div className="mb-2">
              <Link to={routes.emailTemplates} className="btn btn-primary">
                <i className="ti ti-square-rounded-plus-filled me-2" />
                Create New Template
              </Link>
            </div>
          </div>
        </div>

        {/* Main Content Card */}
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-3">All Email Templates ({pagination.total})</h4>
          </div>

          {/* Table */}
          <div
            className="card-body p-0 py-3 printable-content"
            ref={componentRef}
          >
            <div className="print-only-header">
              <div className="school-brand">
                <img src={currentBranch?.blackLogoUrl || "/assets/img/icons/logo.png"} alt={campusBrandName(currentBranch)} />
                <strong>{campusBrandName(currentBranch)}{campusBranchLabel(currentBranch) ? ` - ${campusBranchLabel(currentBranch)}` : ""}</strong>
              </div>
              <div className="report-title">Email Log</div>
              <div className="print-date">{new Date().toLocaleDateString("en-GB")}</div>
            </div>
            <Table
              columns={columns}
              dataSource={templates}
              loading={loading}
              pagination={{
                ...pagination,
                onChange: handlePaginationChange,
              }}
            />
          </div>
        </div>
      </div>

      {/* Edit Email Template Modal */}
      <div
        className={`modal fade ${showEditModal ? "show d-block" : ""}`}
        style={{
          backgroundColor: showEditModal ? "rgba(0,0,0,0.5)" : "transparent",
        }}
        id="edit-modal"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Edit Email Template</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                onClick={closeEditModal}
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleUpdateTemplate}>
              <div className="modal-body">
                {selectedTemplate && (
                  <div className="row">
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Email Type <span className="text-danger">*</span>
                        </label>
                        <select
                          name="category"
                          className={`form-control ${
                            formErrors.category ? "is-invalid" : ""
                          }`}
                          value={formData.category}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Select Email Type</option>
                          {emailTypes.map((type) => (
                            <option key={type.id} value={type.id}>
                              {type.name}
                            </option>
                          ))}
                        </select>
                        {formErrors.category && (
                          <div className="invalid-feedback">
                            {formErrors.category}
                          </div>
                        )}
                      </div>

                      <div className="mb-3">
                        <label className="form-label">
                          Template Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          className={`form-control ${
                            formErrors.name ? "is-invalid" : ""
                          }`}
                          placeholder="Enter Template Name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                        />
                        {formErrors.name && (
                          <div className="invalid-feedback">
                            {formErrors.name}
                          </div>
                        )}
                      </div>

                      <div className="mb-3">
                        <label className="form-label">
                          Email Subject <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          name="subject"
                          className={`form-control ${
                            formErrors.subject ? "is-invalid" : ""
                          }`}
                          placeholder="Enter Email Subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          required
                        />
                        {formErrors.subject && (
                          <div className="invalid-feedback">
                            {formErrors.subject}
                          </div>
                        )}
                      </div>

                      <div className="mb-3">
                        <label className="form-label">
                          Available Variables/Tags
                        </label>
                        <div className="d-flex flex-wrap gap-2 mb-2">
                          {availableVariables.map((variable, index) => (
                            <button
                              key={index}
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              onClick={() =>
                                handleVariableSelect(variable.value)
                              }
                            >
                              {variable.label}
                            </button>
                          ))}
                        </div>
                        <div className="p-2 border rounded">
                          <label className="form-label mb-2">
                            Selected Variables:
                          </label>
                          <div className="d-flex flex-wrap gap-2">
                            {formData.variables.map((variable, index) => (
                              <div
                                key={index}
                                className="badge bg-primary d-flex align-items-center"
                              >
                                {variable}
                                <button
                                  type="button"
                                  className="btn-close btn-close-white ms-2"
                                  style={{ fontSize: "10px" }}
                                  onClick={() => removeVariable(index)}
                                ></button>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="mb-3">
                        <label className="form-label">
                          Email Body <span className="text-danger">*</span>
                        </label>
                        <textarea
                          name="htmlContent"
                          className={`form-control ${
                            formErrors.htmlContent ? "is-invalid" : ""
                          }`}
                          rows={8}
                          placeholder="Enter email body content..."
                          value={formData.htmlContent}
                          onChange={handleInputChange}
                          required
                        />
                        {formErrors.htmlContent && (
                          <div className="invalid-feedback">
                            {formErrors.htmlContent}
                          </div>
                        )}
                      </div>

                      <div className="mb-3">
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="status-title">
                            <h5>Status</h5>
                            <p>Change the Status by toggle</p>
                          </div>
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              id="editIsActive"
                              name="isActive"
                              checked={formData.isActive}
                              onChange={handleInputChange}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="editIsActive"
                            >
                              Active
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  onClick={closeEditModal}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Updating...
                    </>
                  ) : (
                    "Update Template"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Delete Email Template Modal */}
      <div
        className={`modal fade ${showDeleteModal ? "show d-block" : ""}`}
        style={{
          backgroundColor: showDeleteModal ? "rgba(0,0,0,0.5)" : "transparent",
        }}
        id="delete-modal"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body text-center">
              <span className="delete-icon">
                <i className="ti ti-trash-x" />
              </span>
              <h4>Confirm Deletion</h4>
              <p>
                Are you sure you want to delete the template "
                {templateToDelete?.name}"? This action cannot be undone.
              </p>
              <div className="d-flex justify-content-center">
                <button
                  type="button"
                  className="btn btn-light me-3"
                  onClick={closeDeleteModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteConfirm}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Deleting...
                    </>
                  ) : (
                    "Yes, Delete"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailTemplatesList;
